import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Button } from '../ui/button';
import SectionHeading from '../ui/SectionHeading';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const HERITAGE_IMAGE = '/projects/project-bardstown-tower.jpg';

export default function HeritageSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const navigate = useNavigate();

  return (
    <section
      id="heritage"
      className="relative py-20 sm:py-24 lg:py-32 bg-background overflow-hidden"
      data-testid="heritage-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-grain opacity-[0.025] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-4xl"
        >
          <p className="kicker mb-4" data-testid="heritage-kicker">Our Heritage · Since 1999</p>
          <SectionHeading tone="light" className="max-w-3xl" data-testid="heritage-headline">
            Supporting
            <span className="italic font-bold text-forest"> Critical </span>
            Infrastructure From the Ground Up
          </SectionHeading>
        </motion.div>

        {/* Values graphic — Our Purpose / Our Standard / Our Vision */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-10 max-w-6xl mx-auto rounded-xl overflow-hidden border border-foreground/10 shadow-elev-2"
          data-testid="heritage-values"
        >
          <img
            src="/brand/heritage-values.jpg"
            alt="Ozark Mountain Construction values — Our Purpose: build infrastructure people can depend on. Our Standard: do the work right, stand behind it, build it to last. Our Vision: build a company whose reputation lasts as long as the infrastructure we leave behind."
            className="w-full h-auto block"
            loading="lazy"
          />
        </motion.div>

        {/* Paragraphs + picture running beside them */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Story copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-6"
            data-testid="heritage-copy"
          >
            <p className="text-base sm:text-[17px] text-foreground/80 leading-[1.85]">
              Since 1999, Ozark Mountain Construction has specialized in complex
              civil, mechanical, and infrastructure construction for critical water,
              industrial, and energy facilities. Headquartered in Conway, Arkansas,
              OMC has successfully completed more than 500 projects across 30 states,
              partnering with many of the nation&rsquo;s leading engineers,
              municipalities, utilities, and tank fabricators to deliver
              infrastructure with precision, safety, and uncompromising quality.
            </p>
            <p className="text-base sm:text-[17px] text-foreground/80 leading-[1.85]">
              Our work extends far beyond the structure itself. OMC constructs the
              reinforced concrete foundations, underground utilities, mechanical
              piping, pumps, valves, treatment systems, concrete vaults, fiberglass
              equipment enclosures, and supporting site infrastructure that transform
              individual components into fully operational facilities. From initial
              site development through testing, disinfection, startup, commissioning,
              and final system integration, our teams deliver the specialized
              expertise required to bring complex infrastructure into service safely
              and efficiently.
            </p>
            <p className="text-base sm:text-[17px] text-foreground/80 leading-[1.85]">
              Our experience spans municipal water systems, composite elevated tanks,
              multi-column elevated tanks, pedespheres, HydroPillars, fluted-column
              tanks, and ground storage tanks ranging from 50,000 to 5 million gallons,
              as well as pump stations, booster stations, water treatment facilities,
              chemical feed systems, reverse osmosis systems, chlorination equipment,
              and transmission and distribution infrastructure. Beyond the water
              sector, OMC also delivers specialized civil and mechanical construction
              for some of the nation&rsquo;s most demanding industrial and energy
              facilities, including ammonia and fertilizer plants, chemical processing
              facilities, petroleum terminals, manufacturing operations, military
              installations, substations, thermal energy storage facilities, and other
              mission-critical infrastructure where precision, safety, and reliability
              are essential.
            </p>
            <p className="text-base sm:text-[17px] text-foreground/80 leading-[1.85]">
              Every project is executed in accordance with applicable AWWA, ACI,
              engineering, and project-specific requirements. Using advanced robotic
              total-station surveying and precision anchor-bolt template systems, our
              crews routinely achieve installation tolerances of less than one-eighth
              of an inch, ensuring the accuracy required for successful tank erection,
              equipment installation, and long-term structural performance. Every
              project reflects the same commitment that has defined Ozark Mountain
              Construction since 1999: exceptional craftsmanship, uncompromising
              quality, and infrastructure built to stand the test of time.
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                onClick={() => navigate('/about')}
                className="btn-lift bg-primary hover:bg-[hsl(var(--hunter-2))] gap-2 rounded-xl px-6"
                data-testid="heritage-read-story-btn"
              >
                Read our story
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  const el = document.getElementById('capabilities');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-foreground hover:text-primary hover:bg-transparent gap-1.5"
                data-testid="heritage-view-capabilities-btn"
              >
                View capabilities
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </motion.div>

          {/* Picture column — beside the paragraphs */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
            className="space-y-5"
            data-testid="heritage-media"
          >
            {/* Bardstown tower photo */}
            <div className="rounded-xl overflow-hidden shadow-elev-2 border border-bone-2">
              <div className="aspect-[4/3]">
                <img
                  src={HERITAGE_IMAGE}
                  alt="Aerial view of the OMC-built Bardstown, Kentucky elevated water tower and its reinforced concrete foundation"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Engineering renderings */}
            <div className="grid grid-cols-2 gap-4 lg:gap-5" data-testid="heritage-renderings">
              <figure
                className="flex flex-col rounded-lg overflow-hidden border border-border bg-white shadow-elev-1"
                data-testid="heritage-rendering-plan"
              >
                <div className="aspect-[3/4] flex items-center justify-center p-3">
                  <img
                    src="/renderings/tank-plan.jpg"
                    alt="Engineering plan view of a fluted-column composite elevated tank foundation — inlet/outlet pipe, thrust block, anchor bolts, and slab-on-grade"
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <figcaption className="px-3 py-2 border-t border-border font-mono text-[9px] tracking-[0.16em] uppercase text-muted-foreground text-center">
                  Foundation Plan
                </figcaption>
              </figure>
              <figure
                className="flex flex-col rounded-lg overflow-hidden border border-border bg-white shadow-elev-1"
                data-testid="heritage-rendering-cutaway"
              >
                <div className="aspect-[3/4] flex items-center justify-center p-3">
                  <img
                    src="/renderings/tank-cutaway.jpg"
                    alt="3D cutaway rendering of a fluted-column composite elevated tank showing interior riser piping, valve room, and foundation"
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <figcaption className="px-3 py-2 border-t border-border font-mono text-[9px] tracking-[0.16em] uppercase text-muted-foreground text-center">
                  Tank Cutaway
                </figcaption>
              </figure>
            </div>

            {/* Pull-quote card */}
            <div className="editorial-card p-6 lg:p-7" data-testid="heritage-pullquote">
              <span className="font-display text-5xl text-primary leading-none block mb-2">“</span>
              <p className="font-display text-lg sm:text-xl italic text-foreground/90 leading-snug mb-4">
                Anybody can build for today. Building critical infrastructure that has
                to perform for the next fifty years takes the right people, the right
                experience, and doing the job right the first time.
              </p>
              <p className="kicker" data-testid="heritage-pullquote-attribution">
                — OMC Field Operations
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
